/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

/*
* Add new layer to the map
* User can select which bands to visualize as RGB
*/

import * as Blockly from 'blockly/core';
import 'blockly/python';
import { getAllowedOutputs } from '../block_compatibility_checks';
import { ImageService } from '@/services/image.service';
import { getIndexOfValueInDropdownOptions } from '../helper_functions';

let dropdown = [
    ["None", "none"]
]

Blockly.Blocks['output_band_selection'] = {
    init: function () {
        this.image = null;
        this.rootBlockID = this.getRootBlock().id;
        this.bands = this.getBandsFromParent(this);
        
        this.appendDummyInput()
            .appendField("Save image as: ")
            .appendField(new Blockly.FieldTextInput("Default", this.validateImageName), "imageName")

        this.appendDummyInput('bandsInput')
            .appendField("Band selected: ")
            .appendField(new Blockly.FieldDropdown(dropdown, this.validateBandDropdown), "bands")
            
        
        this.setStyle('mapVisualisation');
        this.setPreviousStatement(true, 'band_selection');
        this.setNextStatement(true, getAllowedOutputs('band_selection'));
    },

    /**
     * When the selected band is updated, verify this is a new band option compared to
     * the stored image and update the image service
     * @param {*} newValue - selected band
     */
    validateBandDropdown: function(newValue) {
        let block = this.getSourceBlock();
        if(newValue === 'none') {
            block.setWarningText("Please select a band", "band_error");
        } else {
            block.setWarningText(null, "band_error");
        }
        if(block.image) {
            if(newValue !== block.image.userSelectedBand) {
                block.image.userSelectedBand = newValue;
                block.updateImageService();
                return newValue;
            }
        }
    },

    /**
     * called by the image name field whenever a new image name is input
     * will check if the new value is different to the one in the block image
     * then checks the name is unique to the entire service
     * then updates the local and image service
     * @returns {String/null} validated value
     */
    validateImageName: function(newValue) {
        let block = this.getSourceBlock()
        // name has changed
        if(block.image){
            if(newValue !== block.image.userSelectedName) {
                // is name unique
                if(ImageService.isImageNameUnique(newValue)){
                    block.image.userSelectedName = newValue;
                    block.updateImageService()
                    return newValue;
                } else {
                    return null;
                }
            }
        }
    },

    /** 
     * Updates bands of block, and sets oldBands to be the previously set bands
     */
    updateBands: function() {
        this.oldBands = this.bands;
        this.bands = this.getBandsFromParent(this);
    },

    /**
     * @returns {Boolean} haveBandsChanged - true when bands from the relevant parent block have changed
     */
    haveBandsChanged: function () {
        for (let i = 0; i < this.oldBands.length; i++) {
            if(this.oldBands[i][0] !== this.bands[i][0] || this.oldBands[i][1] !== this.bands[i][1]) { 
                return true;
            }
        }
        return false;
    },

    hasRootBlockChanged: function() {
        let currentRootBlockID = this.getRootBlock().id;
        if(currentRootBlockID !== this.rootBlockID){
            this.rootBlockID = currentRootBlockID;
            return true;
        } else {
            return false;
        }
    },

    /**
     * checks for root block field "area" and if it exists check if
     * it has changed
     */
    updateImageArea: function() {
        let rootBlock = this.getRootBlock();
        let areaName = null;
    
        if(rootBlock.getField("area")) {
            areaName = rootBlock.getFieldValue("area");
        }
    
        if(this.image.areaName !== areaName) {
            this.image.areaName = areaName;
            this.updateImageService();
        }
    },

    updateBandDropdown(bands){
        let oldValue = this.getFieldValue("bands");
        this.removeInput('bandsInput')
        
        this.appendDummyInput('bandsInput')
            .appendField("Band selected: ")
            .appendField(new Blockly.FieldDropdown(bands, this.validateBandDropdown), "bands")

        // check if old value is in new bands dropdown, and set that,
        // ensures band doesn't get changed if user changes sat for example
        // force update on field
        let index = getIndexOfValueInDropdownOptions(oldValue, bands);
        if(index !== -1) {
            this.setFieldValue(oldValue, 'bands');
        } else {
            this.setFieldValue(bands[0][1], 'bands');
        }
    },

    /**
     * external function that lets the image service update this blocks image
     * @param {*} newImage
     */
    updateBlockImage(newImage) {
        if(newImage.userSelectedName !== this.getFieldValue('imageName')){
            this.setFieldValue(newImage.userSelectedName, 'imageName')
        }
        this.image = newImage;
    },

    updateImageService(){
        // update service with new image items
        ImageService.updateImage(this.image)
    },

    /**
     * https://developers.google.com/blockly/reference/js/Blockly.Block#onchange
     */
    onchange: function (event) {
        // adding checks for the end of a drag event i.e. a block has been moved somewhere
        // e.g. outside of its parent block, change events pick up whenever something
        // in the entire block hierarchy has changed e.g. the user changes a dropdown in the parent
        // block
        // DO NOT ALLOW event.type == 'move' THIS WILL CAUSE HEADACHES
        // event.type == 'create' is also quite janky
        // using finished_loading event to capture when user loads workflow or when a featuredWorkflow is clicked
        if((event.type == 'drag' && !event.isStart) || event.type == 'change' || event.type == 'finished_loading'){
            this.updateBands();
            if (this.haveBandsChanged()) {
                this.updateBandDropdown(this.bands)
            }

            // only update this.image if it exists
            if(this.image){
                if(this.hasRootBlockChanged()) {
                    this.image.rootBlockID = this.getRootBlock().id;
                    this.updateImageService()
                }
                
                this.updateImageArea();
            }

        }
    },
    getBandsFromParent: function(block) {
        let bands = [["None", "none"]];
        let rootBlock = block.getRootBlock()

        let descendants = rootBlock.getDescendants()

        let blockIndex = descendants.indexOf(block);

        // code needs to check upwards for bands
        // if immediate parent has bands, use them
        // else check a level above for bands
        // stop when we hit root block
        // CHECK OUT THE FANCY --i OPERATOR (decrements i)
        
        let i = blockIndex
        // magnificent logic brought to you here. while loops, so magnificent
        while (--i >= 0) {
            if (descendants[i].bands) {
                bands = descendants[i].bands
                return bands
            }
        }
        return bands
    }
};